import { Compass, Leaf, HeartHandshake, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { IMG, PILLARS, TEAM } from "../data/packages";
import { useEnquiry } from "../components/EnquiryModal";

const PILLAR_ICONS = [Compass, Leaf, HeartHandshake, ShieldCheck];

export default function AboutUs() {
  const { openEnquiry } = useEnquiry();

  return (
    <div data-testid="page-about-us">
      <PageHeader
        testid="about-header"
        eyebrow="About Us"
        title="We help you find where you"
        accent="belong."
        description="Cosmos Routes is a team of route obsessives, hosts and storytellers designing journeys across India and the world."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10">
            <img src={IMG.journey} alt="The journey on an open road" className="h-72 w-full object-cover md:h-96" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/60 to-transparent" />
            <div className="absolute inset-0 flex items-center p-8 md:p-16">
              <p className="max-w-2xl font-editorial text-2xl italic leading-snug text-slate-100 md:text-4xl">
                "We don't just plan trips; we help you find where you belong in the universe."
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-20 grid items-start gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Our Story</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-4xl">
              Born from a simple observation —{" "}
              <em className="font-editorial font-medium italic text-comet">the best trips are felt, not photographed.</em>
            </h2>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-400">
              <p>
                Cosmos Routes began with a small crew of travellers who kept noticing the same thing: the
                moments people remember are never the checkpoints. They are the kitchen conversations, the
                wrong turns that became the right ones, the night sky nobody had a lens wide enough for.
              </p>
              <p>
                So we built a travel company around those moments. Every route is scouted in person, every
                host trained to read a group, every stay chosen for its people as much as its view. Today we
                design domestic and international journeys — small-group departures, bespoke private routes
                and everything in between.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-5">
            {PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <Reveal key={p.title} delay={i * 0.07}>
                  <div className="h-full rounded-3xl border border-white/10 bg-obsidian p-6 transition-all duration-500 hover:border-aurora/40" data-testid={`pillar-card-${i}`}>
                    <Icon className="h-5 w-5 text-aurora" />
                    <h3 className="mt-4 font-display text-base font-bold text-slate-50">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal className="mt-24">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">The Route Architects</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
            The people who walk the routes <em className="font-editorial font-medium italic text-comet">first</em>
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-3" data-testid="team-grid">
          {TEAM.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className="group rounded-3xl border border-white/10 bg-obsidian p-8 text-center transition-all duration-500 hover:-translate-y-1.5 hover:border-aurora/40">
                <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-aurora/40 bg-aurora/10 font-display text-xl font-extrabold text-aurora transition-all duration-500 group-hover:shadow-[0_0_30px_rgba(0,245,212,0.25)]">
                  {t.initials}
                </span>
                <h3 className="mt-6 font-display text-lg font-bold text-slate-50">{t.name}</h3>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.role}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <div className="flex flex-col items-center justify-between gap-6 rounded-[2rem] border border-aurora/25 bg-aurora/5 p-9 text-center md:flex-row md:p-12 md:text-left">
            <div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-50 md:text-3xl">
                Come find your route with us
              </h2>
              <p className="mt-3 text-sm text-slate-400">Explore. Experience. Belong.</p>
            </div>
            <Link
              to="/contact-us"
              data-testid="about-contact-btn"
              className="flex shrink-0 items-center gap-2 rounded-full bg-aurora px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,245,212,0.45)] hover:-translate-y-0.5"
            >
              Talk to a Curator <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
